import React, { useEffect, useMemo, useState } from 'react';

const COOKIE_KEY = 'phennx_cookie_preferences';

const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const readPreferences = () => {
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return {
      ...defaultPreferences,
      ...parsed,
      necessary: true,
    };
  } catch (error) {
    return null;
  }
};

const CookieBanner = () => {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    const stored = readPreferences();
    if (stored) {
      setPreferences(stored);
      setHasConsent(true);
      return;
    }
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
      setIsVisible(true);
    };

    window.addEventListener('cookie-settings:open', handleOpenSettings);
    return () => window.removeEventListener('cookie-settings:open', handleOpenSettings);
  }, []);

  const savePreferences = (nextPreferences) => {
    const payload = {
      ...nextPreferences,
      necessary: true,
      consentGiven: true,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(payload));
    setPreferences(payload);
    setHasConsent(true);
    setIsVisible(false);
    setShowSettings(false);
    window.dispatchEvent(new CustomEvent('cookie-preferences:updated', { detail: payload }));
  };

  const handleAcceptAll = () =>
    savePreferences({ necessary: true, analytics: true, marketing: true });

  const handleRejectAll = () =>
    savePreferences({ necessary: true, analytics: false, marketing: false });

  const settingsDescription = useMemo(() => {
    if (!hasConsent) {
      return 'Choose which cookies you allow. Necessary cookies help the site work properly.';
    }
    return 'Update your choices at any time. Necessary cookies are always on.';
  }, [hasConsent]);

  return (
    <>
      {isVisible && !showSettings && (
        <section className="cookie-banner" aria-label="Cookie preferences">
          <div className="cookie-banner__inner">
            <div className="cookie-banner__content">
              <h2 className="cookie-banner__title">We use cookies</h2>
              <p className="cookie-banner__text">
                We use essential cookies to keep the site running, plus optional analytics and marketing cookies
                to improve your experience.
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button className="cookie-btn cookie-btn--ghost" type="button" onClick={handleRejectAll}>
                Reject non-essential
              </button>
              <button className="cookie-btn cookie-btn--secondary" type="button" onClick={() => setShowSettings(true)}>
                Settings
              </button>
              <button className="cookie-btn cookie-btn--primary" type="button" onClick={handleAcceptAll}>
                Accept all
              </button>
            </div>
          </div>
        </section>
      )}

      {showSettings && (
        <div className="cookie-settings" role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title">
          <div className="cookie-settings__backdrop" onClick={() => setShowSettings(false)} />
          <div className="cookie-settings__panel">
            <div className="cookie-settings__header">
              <h2 id="cookie-settings-title">Cookie settings</h2>
              <button
                className="cookie-settings__close"
                type="button"
                onClick={() => setShowSettings(false)}
                aria-label="Close cookie settings"
              >
                ✕
              </button>
            </div>
            <p className="cookie-settings__description">{settingsDescription}</p>

            <div className="cookie-settings__options">
              <label className="cookie-toggle">
                <input type="checkbox" checked disabled />
                <span className="cookie-toggle__label">
                  Necessary cookies
                  <span className="cookie-toggle__detail">Required for core functionality.</span>
                </span>
              </label>

              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(event) =>
                    setPreferences((current) => ({
                      ...current,
                      analytics: event.target.checked,
                    }))
                  }
                />
                <span className="cookie-toggle__label">
                  Analytics cookies
                  <span className="cookie-toggle__detail">Help us understand site usage.</span>
                </span>
              </label>

              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(event) =>
                    setPreferences((current) => ({
                      ...current,
                      marketing: event.target.checked,
                    }))
                  }
                />
                <span className="cookie-toggle__label">
                  Marketing cookies
                  <span className="cookie-toggle__detail">Used to personalize marketing content.</span>
                </span>
              </label>
            </div>

            <div className="cookie-settings__actions">
              <button className="cookie-btn cookie-btn--ghost" type="button" onClick={handleRejectAll}>
                Reject all
              </button>
              <button className="cookie-btn cookie-btn--secondary" type="button" onClick={handleAcceptAll}>
                Accept all
              </button>
              <button
                className="cookie-btn cookie-btn--primary"
                type="button"
                onClick={() =>
                  savePreferences({
                    necessary: true,
                    analytics: preferences.analytics,
                    marketing: preferences.marketing,
                  })
                }
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
